import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Alert } from '@/components/ui/Alert'
import { Spinner } from '@/components/ui/Spinner'
import { GameShell } from '@/features/practice/components/GameShell'
import { usePracticeRun } from '@/features/practice/hooks/usePracticeRun'
import { useInvalidateDailyGame } from '@/features/practice/hooks/useDailyGame'
import { GAME_IDS, type GameId, type PracticeRun, type RunMode } from '@/features/practice/types'

function isGameId(value: string | undefined): value is GameId {
  return value !== undefined && (GAME_IDS as readonly string[]).includes(value)
}

/**
 * `/jeux/:gameId` — la route de partie (SPEC.md, Lot J) : démarre une
 * mécanique (`POST /practice/runs`), puis branche `GameShell`, qui existe
 * déjà et fait tout le travail de déroulé jusqu'à l'écran de fin — ce
 * composant ne fait que le précéder (démarrage) et le suivre (retour au
 * catalogue).
 *
 * `?mode=daily` bascule sur le jeu du jour (`DailyCard` y renvoie) ; sans ce
 * paramètre, c'est une partie d'entraînement (`solo`, SPEC §8) — la sélection
 * des topics reste alors au serveur (`topic_ids` omis), qui retombe sur les
 * centres d'intérêt de l'apprenant.
 *
 * Le run démarré et son éventuelle erreur vivent en état local (`useState`),
 * peuplé depuis la promesse de `usePracticeRun().startRun` plutôt que lus
 * directement sur l'état réactif du hook (`run`/`isStarting`/`startError`) :
 * cette mutation est déclenchée depuis un effet qui ne s'exécute qu'une fois
 * (`hasStartedRef`), et `StrictMode` (dev) rejoue les effets — l'observateur
 * de mutation resouscrit par ce rejeu ne republie pas de façon fiable l'issue
 * d'un appel démarré avant lui. Un `setState` posé directement dans le
 * `.then()`/`.catch()` de la promesse retournée n'a pas ce problème.
 */
export function PlayPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { gameId } = useParams<{ gameId: string }>()
  const [searchParams] = useSearchParams()
  const mode: RunMode = searchParams.get('mode') === 'daily' ? 'daily' : 'solo'

  const { startRun } = usePracticeRun()
  const invalidateDailyGame = useInvalidateDailyGame()

  const [run, setRun] = useState<PracticeRun | null>(null)
  const [startError, setStartError] = useState<boolean>(false)

  // Une seule partie par montage : `StrictMode` (dev) rejoue les effets, et
  // `startRun` n'est pas idempotent (`POST /practice/runs` en créerait une
  // deuxième, refusée avec `DAILY_ALREADY_PLAYED` en mode `daily`).
  const hasStartedRef = useRef(false)

  useEffect(() => {
    if (!isGameId(gameId) || hasStartedRef.current) return
    hasStartedRef.current = true

    startRun({ game: gameId, mode })
      .then((startedRun) => setRun(startedRun))
      .catch(() => setStartError(true))
    // La partie ne doit démarrer qu'une fois, au montage — jamais rejouée sur
    // un changement de `mode`/`startRun` (nouvelle closure à chaque rendu).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId])

  const goToCatalog = () => {
    // La partie `daily` vient de se terminer (ou d'être abandonnée) : le jeu
    // du jour en cache (`DailyCard`) doit refléter le nouveau résultat, pas
    // rester sur « à jouer ».
    if (mode === 'daily') invalidateDailyGame()
    navigate('/jeux')
  }

  if (!isGameId(gameId)) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-4 px-4 py-10 text-center">
        <Alert variant="error">{t('practice.play.unknown_game')}</Alert>
        <button
          type="button"
          onClick={goToCatalog}
          className="min-h-[44px] rounded-full bg-primary px-5 text-sm font-semibold text-white"
        >
          {t('practice.play.back_to_catalog')}
        </button>
      </div>
    )
  }

  if (startError) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-4 px-4 py-10 text-center">
        <Alert variant="error">{t('practice.play.start_error')}</Alert>
        <button
          type="button"
          onClick={goToCatalog}
          className="min-h-[44px] rounded-full bg-surface-low px-5 text-sm font-medium text-on-surface"
        >
          {t('practice.play.back_to_catalog')}
        </button>
      </div>
    )
  }

  if (!run) {
    return (
      <div className="flex min-h-full flex-col items-center justify-center gap-3 px-4 py-10">
        <Spinner size="lg" />
        <p className="text-sm text-on-surface-variant">{t('practice.play.starting')}</p>
      </div>
    )
  }

  return (
    <div className="min-h-full overflow-y-auto bg-surface px-container-px-mobile pb-20 pt-6 md:px-container-px-desktop md:pb-8">
      <div className="mx-auto max-w-2xl">
        <GameShell run={run} onAbandon={goToCatalog} onExit={goToCatalog} />
      </div>
    </div>
  )
}
